import axios, { AxiosError } from "axios";
import { env } from "../config/env";
import { network, storage } from "../platform";

const KEYS = { access: "fp.access", refresh: "fp.refresh" };
let accessToken: string | null = null;
let onAuthLost: () => void = () => {};

export const tokens = {
  async load() { accessToken = await storage.get(KEYS.access); return accessToken; },
  get: () => accessToken,
  async set(access: string, refresh: string) {
    accessToken = access;
    await storage.set(KEYS.access, access);
    await storage.set(KEYS.refresh, refresh);
  },
  async clear() { accessToken = null; await storage.remove(KEYS.access); await storage.remove(KEYS.refresh); },
  refresh: () => storage.get(KEYS.refresh),
};

export const setAuthLostHandler = (fn: () => void) => { onAuthLost = fn; };

export const api = axios.create({ baseURL: env.apiBase, timeout: 20000 });

api.interceptors.request.use((cfg) => {
  if (accessToken) cfg.headers.Authorization = `Bearer ${accessToken}`;
  return cfg;
});

let refreshing: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = (async () => {
      const rt = await tokens.refresh();
      if (!rt) return null;
      try {
        const { data } = await axios.post(`${env.apiBase}/auth/refresh`, { refresh_token: rt });
        await tokens.set(data.access_token, data.refresh_token);
        return data.access_token as string;
      } catch {
        return null;
      }
    })().finally(() => { refreshing = null; });
  }
  return refreshing;
}

api.interceptors.response.use((r) => r, async (error: AxiosError) => {
  const original: any = error.config;
  if (error.response?.status === 401 && original && !original._retry && !String(original.url).includes("/auth/")) {
    original._retry = true;
    const t = await refreshAccessToken();
    if (t) {
      original.headers.Authorization = `Bearer ${t}`;
      return api(original);
    }
    await tokens.clear();
    onAuthLost();
  }
  return Promise.reject(error);
});

export function errorMessage(e: any): string {
  if (!e?.response) return network.isOnline() ? "Cannot reach FASTPASS servers. Please try again." : "You are offline.";
  const d = e.response.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return "Something went wrong. Please try again.";
}
